import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocationScope } from "@/context/LocationContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  DollarSign,
  ParkingCircle,
  AlertTriangle,
  Gauge,
  ArrowRight,
  Send,
  ShieldCheck,
  ArrowDownUp,
} from "lucide-react";
import { toast } from "sonner";
import TicketSearch from "@/components/TicketSearch";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const HEADER_IMG = "https://images.pexels.com/photos/1606957/pexels-photo-1606957.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const NEAR_FULL = 85;

export default function OwnerDashboard() {
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState([]);
  const [trendLot, setTrendLot] = useState("all");
  const [running, setRunning] = useState(false);
  const [sortMode, setSortMode] = useState("attention");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setSelectedLot } = useLocationScope();

  const load = async () => {
    const { data: pf } = await api.get("/dashboard/portfolio");
    setData(pf);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const url = trendLot === "all" ? "/dashboard/trends?days=14" : `/dashboard/trends?days=14&lotId=${trendLot}`;
    api.get(url).then(({ data }) => setTrends(data));
  }, [trendLot]);

  const openLot = (lotId) => {
    setSelectedLot(lotId);
    navigate("/board");
  };

  const runEngine = async () => {
    setRunning(true);
    try {
      const { data: r } = await api.post("/engine/run");
      toast.success(`Engine ran: ${r.remindersSent} reminders, ${r.overstaysCharged} overstays charged`);
      load();
    } catch {
      toast.error("Could not run engine");
    } finally {
      setRunning(false);
    }
  };

  if (!data) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[#A0148C]" />
      </div>
    );
  }

  const t = data.totals;
  const sortedLots = [...data.lots].sort((a, b) => {
    if (sortMode === "alpha") return a.name.localeCompare(b.name);
    const rank = (l) => (l.activeOverstays > 0 ? 0 : l.occupancyPct >= NEAR_FULL ? 1 : 2);
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    if (ra === 0) return b.activeOverstays - a.activeOverstays;
    if (ra === 1) return b.occupancyPct - a.occupancyPct;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="px-4 py-6 md:px-8">
      {/* Header banner */}
      <div className="relative mb-8 overflow-hidden rounded-2xl border border-slate-800">
        <img src={HEADER_IMG} alt="Truck yard at sunset" className="h-44 w-full object-cover md:h-56" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-slate-950/30" />
        <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A0148C]">Portfolio</p>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-50 md:text-4xl">
            {user?.role === "owner" ? "Owner Command Center" : "My Locations"}
          </h1>
          <p className="mt-1 text-slate-300">Live visibility across every lot.</p>
        </div>
        {user?.role === "owner" && (
          <Button
            data-testid="run-engine-button"
            onClick={runEngine}
            disabled={running}
            className="absolute right-4 top-4 gap-2 bg-[#A0148C] hover:bg-[#80106F]"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Run billing engine
          </Button>
        )}
      </div>

      <div className="mb-6">
        <TicketSearch />
      </div>

      {/* Overstay revenue recovered — the owner's ROI metric */}
      <Card
        data-testid="kpi-recovered"
        className="mb-6 flex flex-col items-start justify-between gap-4 border-l-4 border-l-[#A0148C] border-slate-800 bg-slate-900 p-6 sm:flex-row sm:items-center"
      >
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#A0148C]">
            <ShieldCheck className="h-7 w-7 text-white" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-300">
              Overstay revenue recovered
            </p>
            <p className="font-data text-4xl font-black text-white">
              ${t.overstayRecoveredWeek.toFixed(2)}
              <span className="ml-2 text-base font-semibold text-slate-400">this week</span>
            </p>
          </div>
        </div>
        <div className="rounded-lg border border-slate-700 bg-slate-950/60 px-5 py-3 text-right">
          <p className="text-xs uppercase tracking-wider text-slate-400">Recovered today</p>
          <p className="font-data text-2xl font-bold text-emerald-400">
            ${t.overstayRecoveredToday.toFixed(2)}
          </p>
        </div>
      </Card>

      {/* Totals */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard icon={DollarSign} label="Today's Revenue" value={`$${t.revenue.toFixed(2)}`} accent="text-emerald-400" testid="kpi-revenue" />
        <KpiCard icon={ParkingCircle} label="Available Spots" value={t.available} accent="text-slate-100" testid="kpi-available" />
        <KpiCard icon={Gauge} label="Occupancy" value={`${t.occupancyPct}%`} accent="text-[#A0148C]" testid="kpi-occupancy" />
        <KpiCard
          icon={AlertTriangle}
          label="Active Overstays"
          value={t.overstays}
          accent={t.overstays > 0 ? "text-red-400" : "text-slate-100"}
          testid="kpi-overstays"
        />
      </div>

      {/* Portfolio trends */}
      {trends.length > 0 && (
        <Card data-testid="portfolio-trends" className="mb-8 border-slate-800 bg-slate-900 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold text-slate-100">Revenue &amp; occupancy — last 14 days</h2>
            <select
              data-testid="trends-lot-select"
              value={trendLot}
              onChange={(e) => setTrendLot(e.target.value)}
              className="h-10 rounded-md border border-slate-700 bg-slate-950 px-3 text-sm text-slate-100"
            >
              <option value="all">All lots</option>
              {data.lots.map((l) => (
                <option key={l.lotId} value={l.lotId}>{l.name}</option>
              ))}
            </select>
          </div>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trends} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="label" stroke="#64748b" fontSize={12} />
                <YAxis yAxisId="left" stroke="#64748b" fontSize={12} />
                <YAxis yAxisId="right" orientation="right" stroke="#64748b" fontSize={12} unit="%" domain={[0, 100]} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, color: "#f1f5f9" }} />
                <Legend />
                <Bar yAxisId="left" dataKey="revenue" name="Revenue ($)" fill="#A0148C" radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="occupancyPct" name="Occupancy (%)" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Lot cards */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold text-slate-100">Lots</h2>
        <Button
          data-testid="sort-toggle"
          variant="secondary"
          onClick={() => setSortMode((m) => (m === "attention" ? "alpha" : "attention"))}
          className="gap-2 bg-slate-800 text-slate-200 hover:bg-slate-700"
        >
          <ArrowDownUp className="h-4 w-4" />
          {sortMode === "attention" ? "Needs attention" : "Alphabetical"}
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {sortedLots.map((lot) => (
          <Card
            key={lot.lotId}
            data-testid={`lot-card-${lot.lotId}`}
            className="border-slate-800 bg-slate-900 p-6 transition-transform hover:-translate-y-0.5"
          >
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h3 className="font-heading text-xl font-bold text-slate-50">{lot.name}</h3>
                <p className="text-sm text-slate-400">{lot.address}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                {lot.activeOverstays > 0 && (
                  <span
                    data-testid={`chip-overstay-${lot.lotId}`}
                    className="overstay-pulse flex items-center gap-1 rounded-full bg-red-950/60 px-2.5 py-1 text-xs font-bold text-red-300"
                  >
                    <AlertTriangle className="h-3 w-3" /> {lot.activeOverstays} overstay
                    {lot.activeOverstays > 1 ? "s" : ""}
                  </span>
                )}
                {lot.activeOverstays === 0 && lot.occupancyPct >= NEAR_FULL && (
                  <span
                    data-testid={`chip-nearfull-${lot.lotId}`}
                    className="flex items-center gap-1 rounded-full bg-amber-950/60 px-2.5 py-1 text-xs font-bold text-amber-300"
                  >
                    Near full {lot.occupancyPct}%
                  </span>
                )}
              </div>
            </div>

            <div className="mb-4 grid grid-cols-3 gap-3 text-center">
              <Mini label="Available" value={lot.available} color="text-emerald-300" />
              <Mini label="Occupancy" value={`${lot.occupancyPct}%`} color="text-[#A0148C]" />
              <Mini label="Revenue" value={`$${lot.todayRevenue.toFixed(0)}`} color="text-emerald-400" />
            </div>

            {/* occupancy bar */}
            <div className="mb-4 h-2 w-full overflow-hidden rounded-full bg-slate-800">
              <div className="h-full rounded-full bg-[#A0148C]" style={{ width: `${lot.occupancyPct}%` }} />
            </div>

            <Button
              data-testid={`open-board-${lot.lotId}`}
              variant="secondary"
              onClick={() => openLot(lot.lotId)}
              className="w-full justify-between bg-slate-800 hover:bg-slate-700"
            >
              Open Live Board <ArrowRight className="h-4 w-4" />
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card data-testid={testid} className="border-slate-800 bg-slate-900 p-5">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className={`h-5 w-5 ${accent}`} />
      </div>
      <p className={`font-data text-3xl font-bold ${accent}`}>{value}</p>
    </Card>
  );
}

function Mini({ label, value, color }) {
  return (
    <div>
      <p className={`font-data text-xl font-bold ${color}`}>{value}</p>
      <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
    </div>
  );
}
